import { useState, useEffect, useRef } from 'react';
import { Search, Filter, Star, MapPin, Briefcase, Calendar, DollarSign, Award, ChevronDown, ChevronUp, User, ArrowLeft, MoreVertical, MessageSquare, Send, Plus, X, CheckCircle, Clock, Lock, TrendingUp, Users, Layers, ChevronRight, Heart, ExternalLink, BarChart2, Mail, Inbox, Trash2, CreditCard as Edit3, Archive, ArrowRight, AlertCircle, RefreshCw } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { transitionCandidateStage } from '../../lib/candidatePipeline';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useWorkdayClientTheme } from '../../contexts/WorkdayClientThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import InterviewRequestModal from '../../components/workday-client/InterviewRequestModal';
import WorkdayClientHeader from '../../components/workday-client/WorkdayClientHeader';
import { getBillingState, isProOrGrace, BillingSubscription } from '../../lib/billingState';

// ─── Interfaces ───────────────────────────────────────────────────────────────

interface ConsultantProfile {
  id: string;
  first_name: string;
  last_name: string;
  photo_url: string | null;
  workday_modules: string;
  experience_years: string;
  certifications: string;
  availability: string;
  availability_status: string;
  hourly_rate: string;
  country_region: string;
  project_end_date: string | null;
  status: string;
  title: string;
  email?: string;
  phone?: string;
  resume_url?: string;
  assignment_status?: { status: string; company_name?: string };
}

interface PipelineEntry {
  id: string;
  client_id: string;
  consultant_id: string;
  job_posting_id: string | null;
  stage: PipelineStage;
  notes: string;
  is_favorited: boolean;
  created_at: string;
  updated_at: string;
  consultant?: ConsultantProfile;
}

interface CandidateMessage {
  id: string;
  client_id: string;
  consultant_id: string;
  sender_type: 'client' | 'consultant';
  message_body: string;
  message_type?: string;
  metadata?: {
    application_id?: string;
    job_posting_id?: string;
    job_title?: string;
    company_name?: string;
    proposed_slots?: Array<{ date: string; time: string }>;
    confirmed_slot?: { date: string; time: string };
    slots?: Array<{ date: string; time: string }>;
    accepted_slot?: { date: string; time: string };
    note?: string;
  } | null;
  is_read: boolean;
  created_at: string;
}

interface JobPosting {
  id: string;
  job_title: string;
  requisition_number: string;
  status: string;
}

interface JobApplication {
  id: string;
  job_posting_id: string;
  consultant_id: string;
  first_name: string;
  last_name: string;
  status: string;
  prescreen_status?: string | null;
  applied_at: string;
  is_favorited?: boolean;
  job_title?: string;
  is_pro_posting?: boolean;
}

type PipelineStage = 'sourced' | 'contacted' | 'applied' | 'screening' | 'interview' | 'offer' | 'hired' | 'archived';
type ActiveTab = 'talent' | 'pipeline';

const PIPELINE_STAGES: { key: PipelineStage; label: string; color: string; bg: string; border: string; dot: string }[] = [
  { key: 'sourced',   label: 'Sourced',    color: 'text-gray-600',   bg: 'bg-gray-50',    border: 'border-gray-200', dot: 'bg-gray-400' },
  { key: 'contacted', label: 'Contacted',  color: 'text-blue-700',   bg: 'bg-blue-50',    border: 'border-blue-200', dot: 'bg-blue-500' },
  { key: 'applied',   label: 'Applied',    color: 'text-yellow-700', bg: 'bg-yellow-50',  border: 'border-yellow-200', dot: 'bg-yellow-500' },
  { key: 'screening', label: 'Screening',  color: 'text-orange-700', bg: 'bg-orange-50',  border: 'border-orange-200', dot: 'bg-orange-500' },
  { key: 'interview', label: 'Interview',  color: 'text-purple-700', bg: 'bg-purple-50',  border: 'border-purple-200', dot: 'bg-purple-500' },
  { key: 'offer',     label: 'Offer',      color: 'text-teal-700',   bg: 'bg-teal-50',    border: 'border-teal-200',  dot: 'bg-[#40E0D0]' },
  { key: 'hired',     label: 'Hired',      color: 'text-green-700',  bg: 'bg-green-50',   border: 'border-green-200', dot: 'bg-green-500' },
  { key: 'archived',  label: 'Archived',   color: 'text-gray-500',   bg: 'bg-gray-50',    border: 'border-gray-200',  dot: 'bg-gray-300' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatRelTime(dateStr: string) {
  const ms = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function Avatar({ consultant, size = 'md' }: { consultant: Partial<ConsultantProfile>; size?: 'sm' | 'md' | 'lg' }) {
  const [imgErr, setImgErr] = useState(false);
  const initials = `${consultant.first_name?.charAt(0) || ''}${consultant.last_name?.charAt(0) || ''}`.toUpperCase();
  const sz = size === 'sm' ? 'w-8 h-8 text-xs' : size === 'lg' ? 'w-14 h-14 text-lg' : 'w-10 h-10 text-sm';
  if (consultant.photo_url && !imgErr) {
    return <img src={consultant.photo_url} alt="" onError={() => setImgErr(true)} className={`${sz} rounded-full object-cover border-2 border-[#40E0D0] flex-shrink-0`} />;
  }
  return (
    <div className={`${sz} rounded-full bg-gradient-to-br from-[#40E0D0] to-[#0066FF] flex items-center justify-center text-white font-bold flex-shrink-0`}>
      {initials || <User className="w-4 h-4" />}
    </div>
  );
}

// ─── Dot-menu component ───────────────────────────────────────────────────────

function DotMenu({ items, theme }: { items: { label: string; icon?: React.ReactNode; onClick: () => void; danger?: boolean }[]; theme: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  return (
    <div ref={ref} className="relative">
      <button onClick={e => { e.stopPropagation(); setOpen(o => !o); }} className={`p-1.5 rounded-lg transition-colors ${theme === 'dark' ? 'hover:bg-gray-600 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
        <MoreVertical className="w-4 h-4" />
      </button>
      {open && (
        <div className={`absolute right-0 top-full mt-1 w-48 rounded-xl border shadow-xl z-50 overflow-hidden ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          {items.map((item, i) => (
            <button key={i} onClick={() => { item.onClick(); setOpen(false); }}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm transition-colors ${
                item.danger
                  ? 'text-red-600 hover:bg-red-50'
                  : theme === 'dark' ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'
              }`}>
              {item.icon}<span>{item.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Candidate Messaging Panel ────────────────────────────────────────────────

function CandidateMessagingPanel({ theme, clientId, onPipelineTransition }: { theme: string; clientId: string; onPipelineTransition?: () => void }) {
  const [conversations, setConversations] = useState<{ consultant: Partial<ConsultantProfile>; lastMsg: CandidateMessage | null; unread: number }[]>([]);
  const [activeConsultant, setActiveConsultant] = useState<Partial<ConsultantProfile> | null>(null);
  const [messages, setMessages] = useState<CandidateMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [mode, setMode] = useState<'list' | 'thread' | 'compose'>('list');
  const [newTarget, setNewTarget] = useState<Partial<ConsultantProfile> | null>(null);
  const [allConsultants, setAllConsultants] = useState<Partial<ConsultantProfile>[]>([]);
  const [consultantSearch, setConsultantSearch] = useState('');
  const [searchResults, setSearchResults] = useState<Partial<ConsultantProfile>[]>([]);
  const [collapsed, setCollapsed] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  // Propose new times state
  const [proposeFor, setProposeFor] = useState<CandidateMessage | null>(null);
  const [proposeSlotsDraft, setProposeSlotsDraft] = useState([{ date: '', time: '' }, { date: '', time: '' }, { date: '', time: '' }]);
  const [proposeNoteDraft, setProposeNoteDraft] = useState('');
  const [sendingPropose, setSendingPropose] = useState(false);

  useEffect(() => { fetchConversations(); fetchAllConsultants(); }, [clientId]);
  useEffect(() => { if (endRef.current && mode === 'thread') endRef.current.scrollIntoView({ behavior: 'smooth' }); }, [messages, mode]);

  useEffect(() => {
    const ch = supabase.channel('candidate-msgs-' + clientId)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'candidate_messages', filter: `client_id=eq.${clientId}` }, () => {
        fetchConversations();
        if (activeConsultant) fetchMessages(activeConsultant.id!);
      }).subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [clientId, activeConsultant]);

  const fetchAllConsultants = async () => {
    const { data } = await supabase.from('consultant_profiles').select('id, first_name, last_name, title, photo_url').in('status', ['approved']).is('soft_deleted_at', null).order('first_name');
    setAllConsultants(data || []);
  };

  const fetchConversations = async () => {
    const { data: msgs } = await supabase.from('candidate_messages').select('*').eq('client_id', clientId).order('created_at', { ascending: false });
    if (!msgs) return;
    const consultantIds = [...new Set(msgs.map(m => m.consultant_id))];
    const { data: consultants } = await supabase.from('consultant_profiles').select('id, first_name, last_name, title, photo_url').in('id', consultantIds);
    const convMap = new Map<string, { consultant: Partial<ConsultantProfile>; lastMsg: CandidateMessage; unread: number }>();
    for (const msg of msgs) {
      if (!convMap.has(msg.consultant_id)) {
        const c = consultants?.find(x => x.id === msg.consultant_id) || { id: msg.consultant_id, first_name: 'Unknown', last_name: '' };
        convMap.set(msg.consultant_id, { consultant: c, lastMsg: msg, unread: 0 });
      }
      if (msg.sender_type === 'consultant' && !msg.is_read) {
        convMap.get(msg.consultant_id)!.unread++;
      }
    }
    setConversations([...convMap.values()]);
  };

  const fetchMessages = async (consultantId: string) => {
    const { data } = await supabase.from('candidate_messages').select('*').eq('client_id', clientId).eq('consultant_id', consultantId).order('created_at', { ascending: true });
    setMessages(data || []);
    const unread = (data || []).filter(m => m.sender_type === 'consultant' && !m.is_read);
    for (const m of unread) await supabase.from('candidate_messages').update({ is_read: true }).eq('id', m.id);
    fetchConversations();
  };

  const openConversation = (c: Partial<ConsultantProfile>) => {
    setActiveConsultant(c);
    fetchMessages(c.id!);
    setMode('thread');
  };

  const sendMessage = async () => {
    if (!draft.trim() || !activeConsultant?.id) return;
    setSending(true);
    const { data: inserted } = await supabase.from('candidate_messages').insert({
      client_id: clientId,
      consultant_id: activeConsultant.id,
      sender_type: 'client',
      message_body: draft.trim(),
    }).select('id').single();
    setDraft('');
    // First outbound message transitions candidate to "contacted" if not yet there.
    const result = await transitionCandidateStage({
      clientId,
      consultantId: activeConsultant.id,
      newStage: 'contacted',
      triggeredBy: 'messaging',
      messageId: inserted?.id ?? null,
    });
    if (result.success) onPipelineTransition?.();
    fetchMessages(activeConsultant.id);
    setSending(false);
  };

  const startNewConversation = (c: Partial<ConsultantProfile>) => {
    setActiveConsultant(c);
    setMessages([]);
    setMode('thread');
    setNewTarget(null);
    setConsultantSearch('');
  };

  const sendProposeNewTimes = async () => {
    if (!proposeFor || !activeConsultant?.id) return;
    const validSlots = proposeSlotsDraft.filter(s => s.date && s.time);
    if (validSlots.length === 0) return;
    setSendingPropose(true);
    const slotLabels = validSlots.map(s => `• ${new Date(s.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} at ${s.time}`).join('\n');
    await supabase.from('candidate_messages').insert({
      client_id: clientId,
      consultant_id: activeConsultant.id,
      sender_type: 'client',
      message_body: `Schedule Pre-Screen Interview — ${proposeFor.metadata?.job_title || 'Position'}`,
      message_type: 'prescreen_new_times',
      metadata: {
        application_id: proposeFor.metadata?.application_id,
        job_posting_id: proposeFor.metadata?.job_posting_id,
        job_title: proposeFor.metadata?.job_title,
        company_name: proposeFor.metadata?.company_name,
        proposed_slots: validSlots,
        note: proposeNoteDraft.trim() || undefined,
      },
    });
    if (proposeFor.metadata?.application_id) {
      await supabase.from('job_applications').update({ prescreen_status: 'rescheduling' }).eq('id', proposeFor.metadata.application_id);
    }
    setSendingPropose(false);
    setProposeFor(null);
    setProposeSlotsDraft([{ date: '', time: '' }, { date: '', time: '' }, { date: '', time: '' }]);
    setProposeNoteDraft('');
    fetchMessages(activeConsultant.id);
  };

  useEffect(() => {
    if (!consultantSearch.trim()) { setSearchResults([]); return; }
    const q = consultantSearch.toLowerCase();
    setSearchResults(allConsultants.filter(c => `${c.first_name} ${c.last_name} ${c.title}`.toLowerCase().includes(q)).slice(0, 8));
  }, [consultantSearch, allConsultants]);

  const totalUnread = conversations.reduce((s, c) => s + c.unread, 0);
  const hdr = `px-4 py-3 flex items-center justify-between border-b ${theme === 'dark' ? 'border-gray-700 bg-[#1a2a2a]' : 'border-[#40E0D0]/30 bg-[#e6fafa]'}`;
  const panel = `rounded-xl border overflow-hidden flex flex-col ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-[#40E0D0]/40'} shadow-sm`;

  return (
    <div className={panel} style={{ minHeight: collapsed ? 0 : 480 }}>
      {/* Header */}
      <div className={hdr}>
        <div className="flex items-center gap-2">
          {(mode === 'thread' || mode === 'compose') && (
            <button onClick={() => { setMode('list'); setActiveConsultant(null); setNewTarget(null); setConsultantSearch(''); }} className="p-1 text-[#40E0D0]"><ArrowLeft className="w-4 h-4" /></button>
          )}
          <MessageSquare className="w-4 h-4 text-[#40E0D0]" />
          <span className="font-semibold text-sm">
            {mode === 'thread' && activeConsultant ? `${activeConsultant.first_name} ${activeConsultant.last_name}` :
             mode === 'compose' ? 'New Message' : 'Candidate Messaging'}
          </span>
          {totalUnread > 0 && mode === 'list' && (
            <span className="px-2 py-0.5 bg-[#40E0D0] text-black text-xs font-bold rounded-full">{totalUnread}</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {mode === 'list' && (
            <button onClick={() => setMode('compose')} className="flex items-center gap-1 px-2.5 py-1.5 bg-[#40E0D0] text-black text-xs font-semibold rounded-lg hover:bg-[#40E0D0]/90 transition-colors">
              <Plus className="w-3 h-3" />New
            </button>
          )}
          <button onClick={() => setCollapsed(o => !o)} className={`p-1.5 rounded-lg transition-colors ${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-[#40E0D0]/10'}`}>
            {collapsed ? <ChevronDown className="w-4 h-4 text-[#40E0D0]" /> : <ChevronUp className="w-4 h-4 text-[#40E0D0]" />}
          </button>
        </div>
      </div>

      {!collapsed && (
        <>
          {/* Compose: pick consultant */}
          {mode === 'compose' && (
            <div className="p-4 flex-1 overflow-y-auto">
              <div className={`flex items-start gap-2 p-2.5 rounded-lg mb-3 text-xs ${theme === 'dark' ? 'bg-teal-900/20 border border-teal-800/30 text-teal-300' : 'bg-teal-50 border border-teal-100 text-teal-700'}`}>
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>Once you send a message, the consultant can reply. Consultants cannot initiate contact.</span>
              </div>
              <p className={`text-xs font-medium mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Search for a Workday consultant to message:</p>
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input autoFocus value={consultantSearch} onChange={e => setConsultantSearch(e.target.value)} placeholder="Search consultant by name or title..."
                  className={`w-full pl-9 pr-4 py-2 text-sm rounded-lg border focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent outline-none ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`} />
              </div>
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {searchResults.length === 0 && consultantSearch && (
                  <p className={`text-sm text-center py-4 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>No consultants found</p>
                )}
                {searchResults.map(c => (
                  <button key={c.id} onClick={() => startNewConversation(c)} className={`w-full flex items-center gap-3 p-2.5 rounded-lg text-left transition-colors ${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                    <Avatar consultant={c} size="sm" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{c.first_name} {c.last_name}</p>
                      {c.title && <p className={`text-xs truncate ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{c.title}</p>}
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400 ml-auto flex-shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Conversation thread */}
          {mode === 'thread' && activeConsultant && (
            <div className="flex flex-col flex-1 overflow-hidden">
              <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ maxHeight: 300 }}>
                {messages.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageSquare className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>Start the conversation</p>
                  </div>
                ) : messages.map(msg => {
                  const isPrescreenReq = msg.message_type === 'prescreen_request' || msg.message_type === 'prescreen_new_times';
                  const isPrescreenAvail = msg.message_type === 'prescreen_availability';
                  const isPrescreenConfirmed = msg.message_type === 'prescreen_confirmed';

                  if (isPrescreenReq && msg.sender_type === 'client') {
                    const slots = msg.metadata?.proposed_slots || [];
                    return (
                      <div key={msg.id} className="flex justify-end">
                        <div className={`w-full max-w-[90%] rounded-2xl border-2 border-[#40E0D0]/40 overflow-hidden ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
                          <div className={`px-3 py-2.5 border-b border-[#40E0D0]/20 ${theme === 'dark' ? 'bg-[#40E0D0]/10' : 'bg-[#40E0D0]/10'}`}>
                            <div className="flex items-center gap-2">
                              <Calendar className="w-3.5 h-3.5 text-[#40E0D0]" />
                              <span className="text-xs font-bold">{msg.message_type === 'prescreen_new_times' ? 'New Times Proposed' : 'Pre-Screen Scheduled'}</span>
                            </div>
                            <p className={`text-xs mt-0.5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{msg.metadata?.job_title}</p>
                          </div>
                          <div className="px-3 py-2.5">
                            {slots.length > 0 && (
                              <div className="space-y-1 mb-2">
                                {slots.map((s, i) => (
                                  <div key={i} className={`flex items-center gap-2 text-xs px-2.5 py-1.5 rounded-lg border ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                                    <Calendar className="w-3 h-3 text-[#40E0D0] flex-shrink-0" />
                                    <span className="font-medium">{new Date(s.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                                    <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>at {s.time}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            {msg.metadata?.note && <p className={`text-xs italic mb-1.5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>"{msg.metadata.note}"</p>}
                          </div>
                          <div className={`px-3 pb-2 text-xs ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`}>You · {formatRelTime(msg.created_at)}</div>
                        </div>
                      </div>
                    );
                  }

                  if (isPrescreenAvail && msg.sender_type === 'consultant') {
                    const slots = msg.metadata?.slots || [];
                    const acceptedSlot = msg.metadata?.accepted_slot;
                    return (
                      <div key={msg.id} className="flex justify-start">
                        <div className={`w-full max-w-[90%] rounded-2xl border-2 border-[#40E0D0]/40 overflow-hidden ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
                          <div className={`px-3 py-2.5 border-b border-[#40E0D0]/20 bg-teal-50`}>
                            <div className="flex items-center gap-2">
                              <CheckCircle className="w-3.5 h-3.5 text-teal-500" />
                              <span className="text-xs font-bold text-gray-800">{acceptedSlot ? 'Candidate Accepted a Time' : 'Candidate Submitted Availability'}</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">{activeConsultant.first_name} {activeConsultant.last_name}</p>
                          </div>
                          <div className="px-3 py-2.5">
                            <div className="space-y-1 mb-2.5">
                              {slots.map((s, i) => (
                                <div key={i} className={`flex items-center gap-2 text-xs px-2.5 py-1.5 rounded-lg border ${acceptedSlot && acceptedSlot.date === s.date && acceptedSlot.time === s.time ? 'bg-teal-50 border-teal-300 font-semibold' : theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                                  <Calendar className="w-3 h-3 text-[#40E0D0] flex-shrink-0" />
                                  <span>{new Date(s.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                                  <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>at {s.time}</span>
                                  {acceptedSlot && acceptedSlot.date === s.date && acceptedSlot.time === s.time && <CheckCircle className="w-3 h-3 text-teal-500 ml-auto" />}
                                </div>
                              ))}
                            </div>
                            <button onClick={() => setProposeFor(msg)}
                              className={`w-full py-1.5 rounded-xl text-xs font-semibold border transition-colors flex items-center justify-center gap-1.5 ${theme === 'dark' ? 'border-[#40E0D0] text-[#40E0D0] hover:bg-[#40E0D0]/10' : 'border-[#40E0D0] text-[#0066FF] hover:bg-[#40E0D0]/10'}`}>
                              <RefreshCw className="w-3 h-3" />Propose New Times
                            </button>
                          </div>
                          <div className={`px-3 pb-2 text-xs ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`}>{activeConsultant.first_name} · {formatRelTime(msg.created_at)}</div>
                        </div>
                      </div>
                    );
                  }

                  if (isPrescreenConfirmed) {
                    const slot = msg.metadata?.confirmed_slot;
                    return (
                      <div key={msg.id} className={`flex ${msg.sender_type === 'client' ? 'justify-end' : 'justify-start'}`}>
                        <div className="max-w-[90%] rounded-2xl border-2 border-emerald-300/50 overflow-hidden bg-white shadow-sm">
                          <div className="bg-emerald-50 px-3 py-2.5">
                            <div className="flex items-center gap-2">
                              <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                              <span className="text-xs font-bold text-emerald-800">Interview Confirmed!</span>
                            </div>
                            {slot && <p className="text-xs font-semibold text-emerald-700 mt-0.5">{new Date(slot.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} at {slot.time}</p>}
                            <p className="text-xs text-gray-400 mt-0.5">{formatRelTime(msg.created_at)}</p>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={msg.id} className={`flex ${msg.sender_type === 'client' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${msg.sender_type === 'client' ? 'bg-[#40E0D0] text-black' : theme === 'dark' ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900'}`}>
                        <p className="text-xs font-semibold mb-1 opacity-70">{msg.sender_type === 'client' ? 'You' : `${activeConsultant.first_name} ${activeConsultant.last_name}`} · {formatRelTime(msg.created_at)}</p>
                        <p className="text-sm whitespace-pre-wrap">{msg.message_body}</p>
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>
              <div className={`p-3 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-100'}`}>
                <div className="flex gap-2 items-end">
                  <textarea value={draft} onChange={e => setDraft(e.target.value)} placeholder={`Message ${activeConsultant.first_name}...`} rows={2}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                    className={`flex-1 text-sm rounded-xl border px-3 py-2 resize-none focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent outline-none ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`} />
                  <button onClick={sendMessage} disabled={sending || !draft.trim()} className="w-9 h-9 bg-[#40E0D0] text-black rounded-xl flex items-center justify-center hover:bg-[#40E0D0]/90 disabled:opacity-40 flex-shrink-0 transition-colors">
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Conversations list */}
          {mode === 'list' && (
            <div className="flex-1 overflow-y-auto divide-y" style={{ maxHeight: 400, divideColor: theme === 'dark' ? '#374151' : '#f3f4f6' }}>
              {conversations.length === 0 ? (
                <div className="p-6 text-center">
                  <Mail className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>No conversations yet</p>
                  <button onClick={() => setMode('compose')} className="mt-3 text-[#40E0D0] text-xs hover:underline">Start a conversation</button>
                </div>
              ) : conversations.map(({ consultant, lastMsg, unread }) => (
                <div key={consultant.id} className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${unread > 0 ? (theme === 'dark' ? 'bg-[#40E0D0]/5' : 'bg-[#40E0D0]/5') : ''} ${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}
                  onClick={() => openConversation(consultant)}>
                  <div className="relative">
                    <Avatar consultant={consultant} size="sm" />
                    {unread > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#40E0D0] text-black text-xs font-bold rounded-full flex items-center justify-center">{unread}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm truncate ${unread > 0 ? 'font-semibold' : 'font-medium'}`}>{consultant.first_name} {consultant.last_name}</p>
                    {lastMsg && <p className={`text-xs truncate mt-0.5 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>{lastMsg.sender_type === 'client' ? 'You: ' : ''}{lastMsg.message_body}</p>}
                  </div>
                  {lastMsg && <span className={`text-xs flex-shrink-0 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`}>{formatRelTime(lastMsg.created_at)}</span>}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Propose New Times modal */}
      {proposeFor && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[300] p-4" onClick={() => setProposeFor(null)}>
          <div className={`max-w-md w-full rounded-2xl shadow-2xl overflow-hidden ${theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`} onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-[#0066FF] to-[#40E0D0] p-5 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center"><Calendar className="w-5 h-5 text-white" /></div>
                  <div>
                    <h2 className="text-base font-bold">Propose New Times</h2>
                    <p className="text-white/80 text-xs mt-0.5">{proposeFor.metadata?.job_title}</p>
                  </div>
                </div>
                <button onClick={() => setProposeFor(null)} className="p-2 hover:bg-white/20 rounded-lg transition-colors"><X className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="p-5 space-y-3">
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Suggest up to 3 alternative time slots for the candidate.</p>
              {proposeSlotsDraft.map((slot, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <span className={`text-xs font-semibold w-16 flex-shrink-0 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Option {i + 1}</span>
                  <input type="date" value={slot.date} onChange={e => setProposeSlotsDraft(prev => prev.map((s, j) => j === i ? { ...s, date: e.target.value } : s))}
                    className={`flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent outline-none ${theme === 'dark' ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'}`} />
                  <input type="time" value={slot.time} onChange={e => setProposeSlotsDraft(prev => prev.map((s, j) => j === i ? { ...s, time: e.target.value } : s))}
                    className={`w-28 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent outline-none ${theme === 'dark' ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'}`} />
                </div>
              ))}
              <textarea value={proposeNoteDraft} onChange={e => setProposeNoteDraft(e.target.value)} rows={2} placeholder="Optional note to the candidate..."
                className={`w-full border rounded-xl px-3 py-2.5 text-sm resize-none focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent outline-none ${theme === 'dark' ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'bg-white border-gray-200 placeholder-gray-400'}`} />
              <div className="flex gap-2 pt-1">
                <button onClick={() => setProposeFor(null)} className={`flex-1 py-2.5 rounded-xl text-sm font-medium border transition-colors ${theme === 'dark' ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>Cancel</button>
                <button onClick={sendProposeNewTimes} disabled={sendingPropose || proposeSlotsDraft.every(s => !s.date || !s.time)}
                  className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-[#40E0D0] text-black hover:bg-[#40E0D0]/90 transition-colors disabled:opacity-50">
                  {sendingPropose ? 'Sending...' : 'Send New Times'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Pipeline Column ──────────────────────────────────────────────────────────

function PipelineColumn({ stage, entries, theme, onMove, onRemove, onToggleFav, onOpenMsg, onNote, onViewProfile, navigate }:
  { stage: typeof PIPELINE_STAGES[0]; entries: PipelineEntry[]; theme: string; onMove: (id: string, to: PipelineStage) => void; onRemove: (id: string) => void; onToggleFav: (id: string, v: boolean) => void; onOpenMsg: (c: Partial<ConsultantProfile>) => void; onNote: (e: PipelineEntry) => void; onViewProfile: (id: string) => void; navigate: any }) {
  return (
    <div className={`rounded-xl border flex flex-col ${theme === 'dark' ? 'bg-gray-800/60 border-gray-700' : `${stage.bg} ${stage.border}`} min-w-[220px] max-w-[260px] flex-shrink-0`} style={{ width: 240 }}>
      {/* Stage header */}
      <div className={`flex items-center gap-2 px-3 py-2.5 border-b ${theme === 'dark' ? 'border-gray-700' : stage.border}`}>
        <span className={`w-2.5 h-2.5 rounded-full ${stage.dot} flex-shrink-0`} />
        <span className={`text-xs font-bold uppercase tracking-wide ${stage.color}`}>{stage.label}</span>
        <span className={`ml-auto text-xs font-semibold px-1.5 py-0.5 rounded-full ${theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-white/70 text-gray-600'}`}>{entries.length}</span>
      </div>
      {/* Cards */}
      <div className="flex-1 overflow-y-auto space-y-2 p-2" style={{ minHeight: 80, maxHeight: 480 }}>
        {entries.length === 0 && (
          <p className={`text-xs text-center py-4 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`}>No candidates</p>
        )}
        {entries.map(entry => (
          <div key={entry.id} className={`rounded-lg border p-3 group ${theme === 'dark' ? 'bg-gray-800 border-gray-700 hover:border-gray-600' : 'bg-white border-gray-200 hover:border-[#40E0D0]/40'} transition-all shadow-sm`}>
            <div className="flex items-start gap-2 mb-2">
              <Avatar consultant={entry.consultant || {}} size="sm" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold leading-tight truncate">{entry.consultant?.first_name} {entry.consultant?.last_name}</p>
                <p className={`text-xs truncate mt-0.5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{entry.consultant?.title || 'Workday Consultant'}</p>
              </div>
              <div className="flex items-center gap-0.5 flex-shrink-0">
                <button onClick={() => onToggleFav(entry.id, !entry.is_favorited)} className="p-1 transition-colors">
                  <Heart className={`w-3.5 h-3.5 ${entry.is_favorited ? 'fill-red-500 text-red-500' : 'text-gray-300 hover:text-red-400'}`} />
                </button>
                <DotMenu theme={theme} items={[
                  { label: 'View Profile', icon: <User className="w-3.5 h-3.5" />, onClick: () => onViewProfile(entry.consultant_id) },
                  { label: 'Message', icon: <MessageSquare className="w-3.5 h-3.5" />, onClick: () => entry.consultant && onOpenMsg(entry.consultant) },
                  { label: 'Add Note', icon: <Edit3 className="w-3.5 h-3.5" />, onClick: () => onNote(entry) },
                  ...PIPELINE_STAGES.filter(s => s.key !== entry.stage && s.key !== 'archived').map(s => ({
                    label: `Move to ${s.label}`,
                    icon: <ArrowRight className="w-3.5 h-3.5" />,
                    onClick: () => onMove(entry.id, s.key),
                  })),
                  { label: 'Archive', icon: <Archive className="w-3.5 h-3.5" />, onClick: () => onMove(entry.id, 'archived') },
                  { label: 'Remove', icon: <Trash2 className="w-3.5 h-3.5" />, onClick: () => onRemove(entry.id), danger: true },
                ]} />
              </div>
            </div>
            {entry.consultant?.workday_modules && (
              <div className={`text-xs px-2 py-0.5 rounded-full inline-block mb-2 truncate max-w-full ${theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                {entry.consultant.workday_modules}
              </div>
            )}
            {entry.notes && (
              <p className={`text-xs italic leading-snug mb-2 line-clamp-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>"{entry.notes}"</p>
            )}
            <div className="flex gap-1 mt-1">
              <button onClick={() => onOpenMsg(entry.consultant!)} className="flex-1 flex items-center justify-center gap-1 py-1 text-xs rounded-lg border border-[#40E0D0]/50 text-[#40E0D0] hover:bg-[#40E0D0]/10 transition-colors">
                <Send className="w-3 h-3" />Message
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function WorkdayTalent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, userType } = useAuth();
  const { theme } = useWorkdayClientTheme();

  // Plan check — adminPlan param lets admin test Pro/Free mode
  const adminPlanParam = searchParams.get('adminPlan');
  const [_isPro, setIsPro] = useState(false);
  // planLoading should be false immediately when adminPlanParam is set — no DB call needed
  const [planLoading, setPlanLoading] = useState(() => adminPlanParam === null);
  // isPro is derived synchronously from adminPlanParam when present
  const isPro = adminPlanParam !== null ? adminPlanParam === 'pro' : _isPro;

  // Talent pool state
  const [consultants, setConsultants] = useState<ConsultantProfile[]>([]);
  const [filteredConsultants, setFilteredConsultants] = useState<ConsultantProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedModule, setSelectedModule] = useState('');
  const [selectedAvailability, setSelectedAvailability] = useState('');
  const [minExperience, setMinExperience] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedConsultant, setSelectedConsultant] = useState<ConsultantProfile | null>(null);
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const [activeTab, setActiveTab] = useState<ActiveTab>('talent');

  // Pipeline state
  const [pipelineEntries, setPipelineEntries] = useState<PipelineEntry[]>([]);
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const [activeMsgConsultant, setActiveMsgConsultant] = useState<Partial<ConsultantProfile> | null>(null);
  const [noteEntry, setNoteEntry] = useState<PipelineEntry | null>(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // Applications state
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [jobPostings, setJobPostings] = useState<JobPosting[]>([]);
  const [appSearch, setAppSearch] = useState('');
  const [favApps, setFavApps] = useState<Set<string>>(new Set());
  const [appMenuOpen, setAppMenuOpen] = useState<string | null>(null);
  const [expandedApp, setExpandedApp] = useState<string | null>(null);

  // Pre-screen scheduling state (from pipeline tab)
  const [sendingPrescreen, setSendingPrescreen] = useState<string | null>(null);
  const [prescreenSent, setPrescreenSent] = useState<Set<string>>(new Set());
  // Schedule Pre-Screen modal (initial request)
  const [scheduleFor, setScheduleFor] = useState<JobApplication | null>(null);
  const [scheduleSlots, setScheduleSlots] = useState([{ date: '', time: '' }, { date: '', time: '' }, { date: '', time: '' }]);
  const [scheduleNote, setScheduleNote] = useState('');
  // Confirm slot modal (review submitted availability)
  const [confirmFor, setConfirmFor] = useState<{ app: JobApplication; slots: Array<{ date: string; time: string }>; } | null>(null);
  const [confirmingSlot, setConfirmingSlot] = useState(false);
  const [proposeTimesFor, setProposeTimesFor] = useState<JobApplication | null>(null);
  const [proposeSlots, setProposeSlots] = useState([{ date: '', time: '' }, { date: '', time: '' }, { date: '', time: '' }]);
  const [proposeNote, setProposeNote] = useState('');
  const [sendingPropose, setSendingPropose] = useState(false);
  const [clientCompanyName, setClientCompanyName] = useState('Hiring Company');

  // Pipeline search/filter
  const [pipelineSearch, setPipelineSearch] = useState('');

  // Re-evaluate plan whenever adminPlan param changes (ATS Testing toggle)
  useEffect(() => {
    if (adminPlanParam !== null) {
      setIsPro(adminPlanParam === 'pro');
      setPlanLoading(false);
    }
  }, [adminPlanParam]);

  useEffect(() => {
    if (user) {
      fetchConsultants();
      if (adminPlanParam === null) fetchPlan();
    }
  }, [user, userType, adminPlanParam]);

  useEffect(() => { filterConsultants(); }, [searchTerm, selectedModule, selectedAvailability, minExperience, consultants]);

  useEffect(() => {
    if (activeTab === 'pipeline') { fetchPipelineEntries(); fetchApplications(); fetchJobPostings(); fetchClientCompanyName(); }
  }, [activeTab]);

  const fetchPlan = async () => {
    if (!user) return;
    if (adminPlanParam !== null) {
      setIsPro(adminPlanParam === 'pro');
      setPlanLoading(false);
      return;
    }
    const { data } = await supabase
      .from('billing_subscriptions')
      .select('id, user_id, paypal_subscription_id, paypal_plan_id, status, plan, seats_included, seats_extra, plan_expires_at, downgrade_pending, billing_cycle, activated_at')
      .eq('user_id', user.id)
      .maybeSingle();
    setIsPro(isProOrGrace(data as BillingSubscription | null));
    setPlanLoading(false);
  };

  const fetchConsultants = async () => {
    try {
      const { data, error } = await supabase
        .from('consultant_profiles')
        .select('id, first_name, last_name, photo_url, workday_modules, experience_years, certifications, availability, availability_status, hourly_rate, country_region, project_end_date, status, title, phone, email, resume_url')
        .eq('status', 'approved')
        .or('is_test_account.is.null,is_test_account.eq.false')
        .is('soft_deleted_at', null)
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (!data || data.length === 0) { setConsultants([]); setFilteredConsultants([]); setLoading(false); return; }

      const ids = data.map(c => c.id);
      const { data: assignments } = await supabase
        .from('consultant_assignments')
        .select('consultant_id, status, companies(name)')
        .in('consultant_id', ids).eq('status', 'active');

      const enriched = data.map(c => ({
        ...c,
        assignment_status: assignments?.find(a => a.consultant_id === c.id)
          ? { status: 'active', company_name: (assignments.find(a => a.consultant_id === c.id)!.companies as any)?.name }
          : undefined
      }));

      setConsultants(enriched);
      setFilteredConsultants(enriched);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchPipelineEntries = async () => {
    if (!user) return;
    setPipelineLoading(true);
    const { data } = await supabase.from('candidate_pipeline_entries').select('*').eq('client_id', user.id).order('updated_at', { ascending: false });
    if (!data) { setPipelineLoading(false); return; }

    const consultantIds = [...new Set(data.map(e => e.consultant_id))];
    const { data: consultantData } = await supabase.from('consultant_profiles').select('id, first_name, last_name, title, photo_url, workday_modules, experience_years, hourly_rate, country_region, availability_status').in('id', consultantIds);

    setPipelineEntries(data.map(e => ({ ...e, consultant: consultantData?.find(c => c.id === e.consultant_id) as ConsultantProfile | undefined })));
    setPipelineLoading(false);
  };

  const fetchApplications = async () => {
    if (!user) return;
    const { data: myJobs } = await supabase.from('workday_client_job_postings').select('id, is_pro_posting').eq('customer_id', user.id);
    if (!myJobs || myJobs.length === 0) { setApplications([]); return; }
    const jobIds = myJobs.map(j => j.id);
    const { data } = await supabase
      .from('job_applications')
      .select('id, job_posting_id, consultant_id, first_name, last_name, status, prescreen_status, applied_at')
      .in('job_posting_id', jobIds)
      .order('applied_at', { ascending: false })
      .limit(50);
    const enriched = (data || []).map(app => ({
      ...app,
      is_pro_posting: myJobs.find(j => j.id === app.job_posting_id)?.is_pro_posting || false,
    }));
    setApplications(enriched);
  };

  const fetchJobPostings = async () => {
    if (!user) return;
    const { data } = await supabase.from('workday_client_job_postings').select('id, job_title, requisition_number, status').eq('customer_id', user.id);
    setJobPostings(data || []);
  };

  const filterConsultants = () => {
    let f = [...consultants];
    if (searchTerm) f = f.filter(c => `${c.workday_modules} ${c.certifications} ${c.title}`.toLowerCase().includes(searchTerm.toLowerCase()));
    if (selectedModule) f = f.filter(c => c.workday_modules?.toLowerCase().includes(selectedModule.toLowerCase()));
    if (selectedAvailability) f = f.filter(c => c.availability?.toLowerCase() === selectedAvailability.toLowerCase());
    if (minExperience > 0) f = f.filter(c => {
      const y = c.experience_years || '';
      if (y.includes('10+')) return 10 >= minExperience;
      if (y.includes('6-10')) return 6 >= minExperience;
      if (y.includes('3-5')) return 3 >= minExperience;
      if (y.includes('1-2')) return 1 >= minExperience;
      return false;
    });
    setFilteredConsultants(f);
  };

  const addToPipeline = async (consultant: ConsultantProfile, stage: PipelineStage = 'sourced') => {
    if (!user) return;
    const result = await transitionCandidateStage({
      clientId: user.id,
      consultantId: consultant.id,
      newStage: stage,
      triggeredBy: 'system',
    });
    if (result.success) fetchPipelineEntries();
  };

  const moveStage = async (entryId: string, stage: PipelineStage) => {
    const entry = pipelineEntries.find(e => e.id === entryId);
    if (!entry || !user) return;
    const result = await transitionCandidateStage({
      clientId: user.id,
      consultantId: entry.consultant_id,
      jobPostingId: entry.job_posting_id,
      newStage: stage,
      triggeredBy: 'manual',
    });
    if (result.success) {
      setPipelineEntries(prev => prev.map(e => e.id === entryId ? { ...e, stage } : e));
    }
  };

  const removeEntry = async (entryId: string) => {
    if (!confirm('Remove this candidate from your pipeline?')) return;
    await supabase.from('candidate_pipeline_entries').delete().eq('id', entryId);
    setPipelineEntries(prev => prev.filter(e => e.id !== entryId));
  };

  const toggleFav = async (entryId: string, val: boolean) => {
    await supabase.from('candidate_pipeline_entries').update({ is_favorited: val }).eq('id', entryId);
    setPipelineEntries(prev => prev.map(e => e.id === entryId ? { ...e, is_favorited: val } : e));
  };

  const saveNote = async () => {
    if (!noteEntry) return;
    setSavingNote(true);
    await supabase.from('candidate_pipeline_entries').update({ notes: noteDraft, updated_at: new Date().toISOString() }).eq('id', noteEntry.id);
    setPipelineEntries(prev => prev.map(e => e.id === noteEntry.id ? { ...e, notes: noteDraft } : e));
    setSavingNote(false);
    setNoteEntry(null);
    setNoteDraft('');
  };

  const fetchClientCompanyName = async () => {
    if (!user) return;
    const { data } = await supabase.from('workday_client_profiles').select('companies(name)').eq('id', user.id).maybeSingle();
    if ((data as any)?.companies?.name) setClientCompanyName((data as any).companies.name);
  };

  const sendPrescreenRequest = async () => {
    if (!user || !scheduleFor) return;
    const validSlots = scheduleSlots.filter(s => s.date && s.time);
    setSendingPrescreen(scheduleFor.id);
    const posting = jobPostings.find(j => j.id === scheduleFor.job_posting_id);
    const { error } = await supabase.from('candidate_messages').insert({
      client_id: user.id,
      consultant_id: scheduleFor.consultant_id,
      sender_type: 'client',
      message_body: `Schedule Pre-Screen Interview — ${posting?.job_title || 'Position'}`,
      message_type: 'prescreen_request',
      metadata: { application_id: scheduleFor.id, job_posting_id: scheduleFor.job_posting_id, job_title: posting?.job_title || 'Position', company_name: clientCompanyName, proposed_slots: validSlots.length > 0 ? validSlots : undefined, note: scheduleNote.trim() || undefined },
    });
    if (!error) {
      await supabase.from('job_applications').update({ prescreen_status: 'requested' }).eq('id', scheduleFor.id);
      setApplications(prev => prev.map(a => a.id === scheduleFor!.id ? { ...a, prescreen_status: 'requested' } : a));
      setPrescreenSent(prev => new Set([...prev, scheduleFor!.id]));
      // Prescreen request moves candidate to screening stage.
      await transitionCandidateStage({
        clientId: user.id,
        consultantId: scheduleFor.consultant_id,
        jobPostingId: scheduleFor.job_posting_id,
        newStage: 'screening',
        triggeredBy: 'prescreen',
      });
      fetchPipelineEntries();
    }
    setSendingPrescreen(null);
    setScheduleFor(null);
    setScheduleSlots([{ date: '', time: '' }, { date: '', time: '' }, { date: '', time: '' }]);
    setScheduleNote('');
  };

  const openConfirmModal = async (app: JobApplication) => {
    if (!user) return;
    const { data: msgs } = await supabase
      .from('candidate_messages')
      .select('*')
      .eq('client_id', user.id)
      .eq('consultant_id', app.consultant_id)
      .eq('message_type', 'prescreen_availability')
      .order('created_at', { ascending: false })
      .limit(1);
    const slots: Array<{ date: string; time: string }> = msgs?.[0]?.metadata?.slots || [];
    setConfirmFor({ app, slots });
  };

  const confirmPrescreen = async (slot: { date: string; time: string }) => {
    if (!user || !confirmFor) return;
    setConfirmingSlot(true);
    const posting = jobPostings.find(j => j.id === confirmFor.app.job_posting_id);
    const { error } = await supabase.from('candidate_messages').insert({
      client_id: user.id,
      consultant_id: confirmFor.app.consultant_id,
      sender_type: 'client',
      message_body: `Your pre-screen interview for ${posting?.job_title || 'the position'} is confirmed!`,
      message_type: 'prescreen_confirmed',
      metadata: { application_id: confirmFor.app.id, job_posting_id: confirmFor.app.job_posting_id, job_title: posting?.job_title || 'Position', company_name: clientCompanyName, confirmed_slot: slot },
    });
    if (!error) {
      await supabase.from('job_applications').update({ prescreen_status: 'confirmed' }).eq('id', confirmFor.app.id);
      setApplications(prev => prev.map(a => a.id === confirmFor!.app.id ? { ...a, prescreen_status: 'confirmed' } : a));
      // Confirmed interview slot moves candidate to interview stage.
      await transitionCandidateStage({
        clientId: user.id,
        consultantId: confirmFor.app.consultant_id,
        jobPostingId: confirmFor.app.job_posting_id,
        newStage: 'interview',
        triggeredBy: 'interview_confirm',
      });
      fetchPipelineEntries();
    }
    setConfirmingSlot(false);
    setConfirmFor(null);
  };

  const sendProposeNewTimes = async () => {
    if (!user || !proposeTimesFor) return;
    const validSlots = proposeSlots.filter(s => s.date && s.time);
    if (validSlots.length === 0) return;
    setSendingPropose(true);
    const posting = jobPostings.find(j => j.id === proposeTimesFor.job_posting_id);
    await supabase.from('candidate_messages').insert({
      client_id: user.id,
      consultant_id: proposeTimesFor.consultant_id,
      sender_type: 'client',
      message_body: `Schedule Pre-Screen Interview — ${posting?.job_title || 'Position'}`,
      message_type: 'prescreen_new_times',
      metadata: { application_id: proposeTimesFor.id, job_posting_id: proposeTimesFor.job_posting_id, job_title: posting?.job_title || 'Position', company_name: clientCompanyName, proposed_slots: validSlots, note: proposeNote.trim() || undefined },
    });
    await supabase.from('job_applications').update({ prescreen_status: 'rescheduling' }).eq('id', proposeTimesFor.id);
    setApplications(prev => prev.map(a => a.id === proposeTimesFor.id ? { ...a, prescreen_status: 'rescheduling' } : a));
    setSendingPropose(false);
    setProposeTimesFor(null);
    setProposeSlots([{ date: '', time: '' }, { date: '', time: '' }, { date: '', time: '' }]);
    setProposeNote('');
  };

  const getAvailabilityConfig = (status: string, notice: string) => {
    switch (status?.toLowerCase()) {
      case 'available': return { badge: 'bg-green-100 text-green-800 border-green-200', dot: 'bg-green-500', label: 'Available' };
      case 'limited': return { badge: 'bg-yellow-100 text-yellow-800 border-yellow-200', dot: 'bg-yellow-500', label: 'Limited' };
      case 'unavailable': return { badge: 'bg-red-100 text-red-800 border-red-200', dot: 'bg-red-500', label: 'Unavailable' };
      case 'on_project': return { badge: 'bg-amber-100 text-amber-800 border-amber-200', dot: 'bg-amber-500', label: 'On Project' };
      default: return { badge: 'bg-gray-100 text-gray-800 border-gray-200', dot: 'bg-gray-400', label: 'Unknown' };
    }
  };

  // Pipeline stats
  const stageCount = (stage: PipelineStage) => pipelineEntries.filter(e => e.stage === stage).length;
  const totalActive = pipelineEntries.filter(e => e.stage !== 'archived').length;

  // Applications filtered
  const filteredApps = applications.filter(a => {
    const q = appSearch.toLowerCase();
    return !q || `${a.first_name} ${a.last_name}`.toLowerCase().includes(q);
  });

  // Pipeline entries filtered
  const pipelineFiltered = pipelineSearch
    ? pipelineEntries.filter(e => `${e.consultant?.first_name} ${e.consultant?.last_name} ${e.consultant?.title}`.toLowerCase().includes(pipelineSearch.toLowerCase()))
    : pipelineEntries;

  const card = `rounded-xl border ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm`;

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-black text-white' : 'bg-gray-50 text-gray-900'}`}>
      <WorkdayClientHeader />

      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-12">
        {/* Back */}
        <button onClick={() => navigate(adminPlanParam ? `/workday-client/dashboard?adminPlan=${adminPlanParam}` : '/workday-client/dashboard')} className="mb-5 flex items-center text-[#40E0D0] hover:text-[#40E0D0]/80 transition-colors text-sm">
          <ArrowLeft className="w-4 h-4 mr-1.5" />Back to Dashboard
        </button>

        {/* Page header + tabs */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold">Workday Talent Pool</h1>
            <p className={`text-sm mt-0.5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Browse and track Workday consultants</p>
          </div>
          <div className={`flex gap-1 p-1 rounded-xl ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'}`}>
            <button onClick={() => setActiveTab('talent')} className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'talent' ? 'bg-[#40E0D0] text-black shadow-sm' : theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-800'}`}>
              <Users className="w-4 h-4" />Talent Pool
            </button>
            <button onClick={() => setActiveTab('pipeline')} className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'pipeline' ? 'bg-[#40E0D0] text-black shadow-sm' : theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-800'}`}>
              <Layers className="w-4 h-4" />Pipeline &amp; Tracking
              {isPro && totalActive > 0 && (
                <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs font-bold ${activeTab === 'pipeline' ? 'bg-black/20' : 'bg-[#40E0D0]/20 text-[#40E0D0]'}`}>{totalActive}</span>
              )}
            </button>
          </div>
        </div>

        {/* ── TALENT POOL TAB ── */}
        {activeTab === 'talent' && (
          <>
            {/* Search/filter bar */}
            <div className={`mb-5 p-4 rounded-xl ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'} shadow-sm border ${theme === 'dark' ? 'border-gray-800' : 'border-gray-200'}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input type="text" placeholder="Search by skills, modules, or certifications..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                    className={`w-full pl-9 pr-4 py-2 rounded-lg border text-sm ${theme === 'dark' ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'} focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent outline-none`} />
                </div>
                <button onClick={() => setShowFilters(!showFilters)} className="flex items-center gap-2 px-4 py-2 bg-[#40E0D0] text-black rounded-lg hover:bg-[#40E0D0]/90 text-sm font-medium transition-colors">
                  <Filter className="w-4 h-4" />Filters{showFilters ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>
              </div>
              {showFilters && (
                <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 pt-3 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-100'}`}>
                  <div>
                    <label className="block text-xs font-medium mb-1.5">Workday Module</label>
                    <select value={selectedModule} onChange={e => setSelectedModule(e.target.value)} className={`w-full px-3 py-2 rounded-lg border text-sm ${theme === 'dark' ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'}`}>
                      <option value="">All Modules</option>
                      {[...new Set(consultants.map(c => c.workday_modules).filter(Boolean))].map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1.5">Availability</label>
                    <select value={selectedAvailability} onChange={e => setSelectedAvailability(e.target.value)} className={`w-full px-3 py-2 rounded-lg border text-sm ${theme === 'dark' ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'}`}>
                      <option value="">All</option>
                      {[...new Set(consultants.map(c => c.availability).filter(Boolean))].map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1.5">Min Experience (yrs)</label>
                    <input type="number" min="0" value={minExperience} onChange={e => setMinExperience(Number(e.target.value))} className={`w-full px-3 py-2 rounded-lg border text-sm ${theme === 'dark' ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'}`} />
                  </div>
                </div>
              )}
            </div>

            <p className={`text-sm mb-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Showing {filteredConsultants.length} of {consultants.length} consultants</p>

            {loading ? (
              <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#40E0D0]" /></div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredConsultants.map(consultant => {
                  const avail = getAvailabilityConfig(consultant.availability_status, consultant.availability);
                  const inPipeline = pipelineEntries.some(e => e.consultant_id === consultant.id && e.stage !== 'archived');
                  return (
                    <div key={consultant.id} className={`rounded-xl p-5 transition-all hover:shadow-md ${theme === 'dark' ? 'bg-gray-900 border border-gray-800' : 'bg-white border border-gray-200 shadow-sm'}`}>
                      <div className="flex items-start gap-3 mb-4">
                        <Avatar consultant={consultant} size="lg" />
                        <div className="flex-1 min-w-0">
                          {consultant.title && <h3 className="text-base font-semibold mb-1 leading-tight">{consultant.title}</h3>}
                          <div className="flex flex-wrap gap-1.5">
                            {consultant.assignment_status ? (
                              <span className="text-xs px-2 py-0.5 rounded-full border bg-blue-100 text-blue-800 border-blue-200 inline-flex items-center gap-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />On Assignment
                              </span>
                            ) : (
                              <span className={`text-xs px-2 py-0.5 rounded-full border font-medium inline-flex items-center gap-1 ${avail.badge}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${avail.dot}`} />{avail.label}
                              </span>
                            )}
                          </div>
                        </div>
                        {/* 3-dot menu */}
                        <DotMenu theme={theme} items={[
                          { label: 'View Profile', icon: <User className="w-3.5 h-3.5" />, onClick: () => navigate(`/workday-client/consultant/${consultant.id}`) },
                          ...(isPro ? [{ label: inPipeline ? 'Already in Pipeline' : 'Add to Pipeline', icon: <Layers className="w-3.5 h-3.5" />, onClick: () => !inPipeline && addToPipeline(consultant) }] : []),
                          { label: 'Request Interview', icon: <Calendar className="w-3.5 h-3.5" />, onClick: () => { setSelectedConsultant(consultant); setShowInterviewModal(true); } },
                        ]} />
                      </div>

                      <div className="space-y-2 mb-3">
                        <div className="flex items-center gap-2 text-sm"><Briefcase className="w-3.5 h-3.5 text-[#40E0D0]" /><span>{consultant.experience_years || 'N/A'} experience</span></div>
                        {consultant.country_region && <div className="flex items-center gap-2 text-sm"><MapPin className="w-3.5 h-3.5 text-[#40E0D0]" /><span>{consultant.country_region}</span></div>}
                        {consultant.hourly_rate && <div className="flex items-center gap-2 text-sm"><DollarSign className="w-3.5 h-3.5 text-[#40E0D0]" /><span>${consultant.hourly_rate}/hr</span></div>}
                        {consultant.project_end_date && <div className="flex items-center gap-2 text-sm"><Calendar className="w-3.5 h-3.5 text-[#40E0D0]" /><span>Avail. {new Date(consultant.project_end_date).toLocaleDateString()}</span></div>}
                      </div>

                      {consultant.workday_modules && (
                        <div className="mb-3">
                          <div className="flex flex-wrap gap-1">
                            {consultant.workday_modules.split(',').slice(0, 3).map((m, i) => (
                              <span key={i} className={`text-xs px-2 py-0.5 rounded-full ${theme === 'dark' ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>{m.trim()}</span>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="flex gap-2 mt-3">
                        <button onClick={() => navigate(`/workday-client/consultant/${consultant.id}`)} className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${theme === 'dark' ? 'border-gray-700 hover:bg-gray-800' : 'border-gray-300 hover:bg-gray-50'}`}>
                          View Profile
                        </button>
                        {isPro ? (
                          <button onClick={() => addToPipeline(consultant)} disabled={inPipeline} className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${inPipeline ? (theme === 'dark' ? 'bg-gray-700 text-gray-500' : 'bg-gray-100 text-gray-400') : 'bg-[#40E0D0] text-black hover:bg-[#40E0D0]/90'}`}>
                            {inPipeline ? 'In Pipeline' : '+ Pipeline'}
                          </button>
                        ) : (
                          <button onClick={() => { setSelectedConsultant(consultant); setShowInterviewModal(true); }} className="flex-1 py-2 bg-[#40E0D0] text-black rounded-lg text-sm font-medium hover:bg-[#40E0D0]/90 transition-colors">
                            Interview
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {!loading && filteredConsultants.length === 0 && (
              <div className={`text-center py-12 rounded-xl ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'} border ${theme === 'dark' ? 'border-gray-800' : 'border-gray-200'}`}>
                <User className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <h3 className="font-semibold mb-1">No consultants found</h3>
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>Try adjusting your search or filters</p>
              </div>
            )}
          </>
        )}

        {/* ── PIPELINE & TRACKING TAB ── */}
        {activeTab === 'pipeline' && (
          <>
            {planLoading ? (
              <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#40E0D0]" /></div>
            ) : !isPro ? (
              /* Pro gate */
              <div className={`rounded-2xl border-2 border-dashed ${theme === 'dark' ? 'border-gray-700 bg-gray-800/50' : 'border-gray-300 bg-white'} p-16 text-center max-w-lg mx-auto mt-8`}>
                <div className="w-16 h-16 rounded-full bg-[#40E0D0]/10 flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-8 h-8 text-[#40E0D0]" />
                </div>
                <h2 className="text-xl font-bold mb-2">Pro Plan Required</h2>
                <p className={`text-sm mb-6 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  Pipeline & Tracking and Candidate Messaging are available exclusively on PerplexityATS Pro ($499.99/mo). Upgrade to track candidates through your hiring pipeline and message any Workday consultant directly.
                </p>
                <button onClick={() => navigate('/workday-client/settings')} className="px-6 py-2.5 bg-[#40E0D0] text-black font-semibold rounded-xl hover:bg-[#40E0D0]/90 transition-colors">
                  Upgrade to Pro
                </button>
              </div>
            ) : (
              /* Two-column layout */
              <div className="flex gap-6" style={{ alignItems: 'flex-start' }}>

                {/* LEFT: Pipeline + Recent Applications */}
                <div className="flex-1 min-w-0 space-y-6">

                  {/* Pipeline stats row */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { label: 'In Pipeline', value: totalActive, icon: <Layers className="w-4 h-4" />, color: 'text-[#40E0D0]' },
                      { label: 'Interviews', value: stageCount('interview'), icon: <Calendar className="w-4 h-4" />, color: 'text-purple-500' },
                      { label: 'Offers Out', value: stageCount('offer'), icon: <Send className="w-4 h-4" />, color: 'text-teal-500' },
                      { label: 'Hired', value: stageCount('hired'), icon: <CheckCircle className="w-4 h-4" />, color: 'text-green-500' },
                    ].map(s => (
                      <div key={s.label} className={`${card} p-4`}>
                        <div className={`${s.color} mb-1.5`}>{s.icon}</div>
                        <div className="text-2xl font-bold">{s.value}</div>
                        <div className={`text-xs mt-0.5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{s.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Kanban board */}
                  <div className={card}>
                    <div className="flex items-center justify-between px-4 py-3 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-100'}">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-[#40E0D0]" />
                        <h3 className="font-semibold text-sm">Hiring Pipeline</h3>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="relative">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                          <input value={pipelineSearch} onChange={e => setPipelineSearch(e.target.value)} placeholder="Search pipeline..." className={`pl-8 pr-3 py-1.5 text-xs rounded-lg border focus:ring-1 focus:ring-[#40E0D0] outline-none ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-200'}`} />
                        </div>
                        <button onClick={fetchPipelineEntries} className={`p-1.5 rounded-lg transition-colors ${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}><RefreshCw className="w-3.5 h-3.5 text-gray-400" /></button>
                        <DotMenu theme={theme} items={[
                          { label: 'View Favorited', icon: <Heart className="w-3.5 h-3.5" />, onClick: () => setPipelineSearch('') },
                          { label: 'Go to Talent Pool', icon: <Users className="w-3.5 h-3.5" />, onClick: () => setActiveTab('talent') },
                        ]} />
                      </div>
                    </div>

                    {pipelineLoading ? (
                      <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#40E0D0]" /></div>
                    ) : (
                      <div className="overflow-x-auto p-4">
                        <div className="flex gap-3" style={{ minWidth: 'max-content' }}>
                          {PIPELINE_STAGES.filter(s => s.key !== 'archived').map(stage => (
                            <PipelineColumn
                              key={stage.key}
                              stage={stage}
                              entries={pipelineFiltered.filter(e => e.stage === stage.key)}
                              theme={theme}
                              onMove={moveStage}
                              onRemove={removeEntry}
                              onToggleFav={toggleFav}
                              onOpenMsg={c => {
                                if (activeMsgConsultant?.id === c.id) {
                                  // already open
                                } else {
                                  setActiveMsgConsultant(c);
                                }
                              }}
                              onNote={e => { setNoteEntry(e); setNoteDraft(e.notes || ''); }}
                              onViewProfile={id => navigate(`/workday-client/consultant/${id}`)}
                              navigate={navigate}
                            />
                          ))}
                        </div>
                        {pipelineEntries.filter(e => e.stage !== 'archived').length === 0 && !pipelineLoading && (
                          <div className="text-center py-8">
                            <Layers className={`w-10 h-10 mx-auto mb-2 ${theme === 'dark' ? 'text-gray-700' : 'text-gray-300'}`} />
                            <p className={`text-sm ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>No candidates in your pipeline yet.</p>
                            <button onClick={() => setActiveTab('talent')} className="mt-3 text-[#40E0D0] text-sm hover:underline">Browse talent pool to add candidates</button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Archived row */}
                  {pipelineEntries.some(e => e.stage === 'archived') && (
                    <div className={card}>
                      <div className="flex items-center justify-between px-4 py-3 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-100'}">
                        <div className="flex items-center gap-2">
                          <Archive className="w-4 h-4 text-gray-400" />
                          <h3 className={`font-semibold text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Archived ({stageCount('archived')})</h3>
                        </div>
                        <DotMenu theme={theme} items={[{ label: 'Clear Archived', icon: <Trash2 className="w-3.5 h-3.5" />, onClick: () => {}, danger: true }]} />
                      </div>
                      <div className="p-3 flex flex-wrap gap-2">
                        {pipelineEntries.filter(e => e.stage === 'archived').map(entry => (
                          <div key={entry.id} className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                            <Avatar consultant={entry.consultant || {}} size="sm" />
                            <span className="text-sm">{entry.consultant?.first_name} {entry.consultant?.last_name}</span>
                            <DotMenu theme={theme} items={[
                              { label: 'Move to Sourced', icon: <ArrowRight className="w-3.5 h-3.5" />, onClick: () => moveStage(entry.id, 'sourced') },
                              { label: 'Remove', icon: <Trash2 className="w-3.5 h-3.5" />, onClick: () => removeEntry(entry.id), danger: true },
                            ]} />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recent Applications */}
                  <div className={card}>
                    <div className="flex items-center justify-between px-4 py-3 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-100'}">
                      <div className="flex items-center gap-2">
                        <BarChart2 className="w-4 h-4 text-[#40E0D0]" />
                        <h3 className="font-semibold text-sm">Recent Applications</h3>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="relative">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                          <input value={appSearch} onChange={e => setAppSearch(e.target.value)} placeholder="Search applicants..." className={`pl-8 pr-3 py-1.5 text-xs rounded-lg border focus:ring-1 focus:ring-[#40E0D0] outline-none ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-200'}`} />
                        </div>
                        <DotMenu theme={theme} items={[
                          { label: 'View All Job Postings', icon: <ExternalLink className="w-3.5 h-3.5" />, onClick: () => navigate('/workday-client/post-jobs') },
                        ]} />
                      </div>
                    </div>
                    <div className={`divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-100'}`}>
                      {filteredApps.length === 0 ? (
                        <div className="text-center py-8">
                          <Users className={`w-8 h-8 mx-auto mb-2 ${theme === 'dark' ? 'text-gray-700' : 'text-gray-300'}`} />
                          <p className={`text-sm ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>No recent applications</p>
                        </div>
                      ) : filteredApps.slice(0, 15).map(app => {
                        const isFav = favApps.has(app.id);
                        const posting = jobPostings.find(j => j.id === app.job_posting_id);
                        const isExpanded = expandedApp === app.id;
                        const appStatusColors: Record<string, string> = {
                          pending: 'bg-yellow-100 text-yellow-700',
                          reviewed: 'bg-blue-100 text-blue-700',
                          shortlisted: 'bg-green-100 text-green-700',
                          interview_scheduled: 'bg-teal-100 text-teal-700',
                          rejected: 'bg-red-100 text-red-700',
                          hired: 'bg-emerald-100 text-emerald-700',
                        };
                        const prescreenColors: Record<string, string> = {
                          requested: 'bg-yellow-100 text-yellow-800 border-yellow-200',
                          availability_submitted: 'bg-blue-100 text-blue-800 border-blue-200',
                          confirmed: 'bg-emerald-100 text-emerald-800 border-emerald-200',
                          rescheduling: 'bg-orange-100 text-orange-800 border-orange-200',
                        };
                        const prescreenLabel: Record<string, string> = {
                          requested: 'Awaiting Availability',
                          availability_submitted: 'Review Availability',
                          confirmed: 'Confirmed',
                          rescheduling: 'Rescheduling',
                        };
                        return (
                          <div key={app.id} className={`transition-colors ${theme === 'dark' ? 'hover:bg-gray-700/30' : 'hover:bg-gray-50/80'}`}>
                            {/* Row header */}
                            <div className="flex items-center gap-3 px-4 py-3">
                              <button onClick={() => navigate(`/workday-client/consultant/${app.consultant_id}`)} className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-[#40E0D0]/20'}`}>
                                <User className="w-4 h-4 text-[#40E0D0]" />
                              </button>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="text-sm font-semibold truncate">{app.first_name} {app.last_name}</p>
                                  {app.is_pro_posting && (
                                    <span className="flex items-center gap-0.5 text-xs px-1.5 py-0.5 bg-[#40E0D0]/20 text-[#40E0D0] rounded-full font-bold"><Star className="w-3 h-3" />Pro</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 flex-wrap mt-0.5">
                                  {posting && <p className={`text-xs truncate ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>{posting.job_title}</p>}
                                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${appStatusColors[app.status] || 'bg-gray-100 text-gray-600'}`}>{app.status.replace(/_/g, ' ')}</span>
                                  {app.prescreen_status && (
                                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold border ${prescreenColors[app.prescreen_status] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                                      Pre-Screen: {prescreenLabel[app.prescreen_status] || app.prescreen_status}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <button onClick={() => setFavApps(prev => { const n = new Set(prev); isFav ? n.delete(app.id) : n.add(app.id); return n; })} className="p-1">
                                  <Heart className={`w-3.5 h-3.5 ${isFav ? 'fill-red-500 text-red-500' : 'text-gray-300 hover:text-red-400'}`} />
                                </button>
                                <button onClick={() => setExpandedApp(isExpanded ? null : app.id)} className={`p-1.5 rounded-lg transition-colors ${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                                  {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                                </button>
                                <DotMenu theme={theme} items={[
                                  { label: 'View Profile', icon: <User className="w-3.5 h-3.5" />, onClick: () => navigate(`/workday-client/consultant/${app.consultant_id}`) },
                                  ...(posting ? [{ label: 'View Job Posting', icon: <ExternalLink className="w-3.5 h-3.5" />, onClick: () => navigate('/workday-client/jobs') }] : []),
                                  { label: 'Add to Pipeline', icon: <Layers className="w-3.5 h-3.5" />, onClick: async () => { const c = consultants.find(x => x.id === app.consultant_id); if (c) addToPipeline(c, 'applied'); } },
                                ]} />
                              </div>
                            </div>

                            {/* Expanded: details + actions */}
                            {isExpanded && (
                              <div className={`px-4 pb-4 pt-1 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-100'}`}>
                                <div className="flex flex-wrap items-center gap-2 text-xs mb-3">
                                  <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Applied {new Date(app.applied_at).toLocaleDateString()}</span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  <button onClick={() => navigate(`/workday-client/consultant/${app.consultant_id}`)} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${theme === 'dark' ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                                    <User className="w-3.5 h-3.5" />View Profile
                                  </button>
                                  {app.is_pro_posting && (
                                    <>
                                      {(!app.prescreen_status || app.prescreen_status === 'rescheduling') ? (
                                        <button
                                          onClick={() => setScheduleFor(app)}
                                          disabled={prescreenSent.has(app.id)}
                                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-[#40E0D0] text-black hover:bg-[#40E0D0]/90 transition-colors disabled:opacity-60"
                                        >
                                          <Calendar className="w-3.5 h-3.5" />
                                          {prescreenSent.has(app.id) ? 'Sent' : 'Schedule Pre-Screen Interview'}
                                        </button>
                                      ) : app.prescreen_status === 'availability_submitted' ? (
                                        <>
                                          <button
                                            onClick={() => openConfirmModal(app)}
                                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 transition-colors"
                                          >
                                            <CheckCircle className="w-3.5 h-3.5" />Review & Confirm Time
                                          </button>
                                          <button
                                            onClick={() => setProposeTimesFor(app)}
                                            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${theme === 'dark' ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                                          >
                                            <RefreshCw className="w-3.5 h-3.5" />Reschedule
                                          </button>
                                        </>
                                      ) : app.prescreen_status === 'requested' ? (
                                        <span className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg ${theme === 'dark' ? 'bg-gray-700 text-gray-400' : 'bg-gray-50 text-gray-500'}`}>
                                          <Clock className="w-3.5 h-3.5" />Awaiting candidate response
                                        </span>
                                      ) : app.prescreen_status === 'confirmed' ? (
                                        <div className="flex items-center gap-2">
                                          <span className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-emerald-100 text-emerald-700">
                                            <CheckCircle className="w-3.5 h-3.5" />Pre-Screen Confirmed
                                          </span>
                                          <button onClick={() => setProposeTimesFor(app)} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-colors ${theme === 'dark' ? 'border-gray-600 text-gray-400 hover:bg-gray-700' : 'border-gray-300 text-gray-500 hover:bg-gray-50'}`}>
                                            <RefreshCw className="w-3 h-3" />Reschedule
                                          </button>
                                        </div>
                                      ) : null}
                                    </>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {filteredApps.length > 15 && (
                      <div className={`px-4 py-2 text-center border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-100'}`}>
                        <button onClick={() => navigate('/workday-client/jobs')} className="text-[#40E0D0] text-xs hover:underline">View all {filteredApps.length} applications in Job Postings</button>
                      </div>
                    )}
                  </div>
                </div>

                {/* RIGHT: Candidate Messaging */}
                <div className="w-80 flex-shrink-0 space-y-4 sticky top-32">
                  <CandidateMessagingPanel theme={theme} clientId={user!.id} onPipelineTransition={fetchPipelineEntries} />

                  {/* Quick pipeline summary */}
                  <div className={card}>
                    <div className="px-4 py-3 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-100'} flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <BarChart2 className="w-4 h-4 text-[#40E0D0]" />
                        <span className="font-semibold text-sm">Stage Summary</span>
                      </div>
                      <DotMenu theme={theme} items={[{ label: 'Refresh', icon: <RefreshCw className="w-3.5 h-3.5" />, onClick: fetchPipelineEntries }]} />
                    </div>
                    <div className="p-3 space-y-1.5">
                      {PIPELINE_STAGES.filter(s => s.key !== 'archived').map(s => {
                        const count = stageCount(s.key);
                        const pct = totalActive > 0 ? Math.round((count / totalActive) * 100) : 0;
                        return (
                          <div key={s.key} className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${s.dot} flex-shrink-0`} />
                            <span className={`text-xs flex-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{s.label}</span>
                            <div className={`h-1.5 rounded-full flex-1 max-w-[80px] overflow-hidden ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}>
                              <div className={`h-full rounded-full ${s.dot}`} style={{ width: `${pct}%` }} />
                            </div>
                            <span className={`text-xs font-semibold w-4 text-right ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Interview modal */}
      {showInterviewModal && selectedConsultant && (
        <InterviewRequestModal consultant={selectedConsultant} onClose={() => { setShowInterviewModal(false); setSelectedConsultant(null); }} theme={theme} />
      )}

      {/* Schedule Pre-Screen Interview modal */}
      {scheduleFor && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4" onClick={() => setScheduleFor(null)}>
          <div className={`max-w-md w-full rounded-2xl shadow-2xl overflow-hidden ${theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`} onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-[#0066FF] to-[#40E0D0] p-5 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center"><Calendar className="w-5 h-5 text-white" /></div>
                  <div>
                    <h2 className="text-base font-bold">Schedule Pre-Screen Interview</h2>
                    <p className="text-white/80 text-xs mt-0.5">{scheduleFor.first_name} {scheduleFor.last_name}</p>
                  </div>
                </div>
                <button onClick={() => setScheduleFor(null)} className="p-2 hover:bg-white/20 rounded-lg transition-colors"><X className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="p-5 space-y-4">
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Propose up to 3 available time slots. The candidate will confirm or propose alternatives.</p>
              {scheduleSlots.map((slot, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <span className={`text-xs font-semibold w-16 flex-shrink-0 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Option {i + 1}</span>
                  <input type="date" value={slot.date} onChange={e => setScheduleSlots(prev => prev.map((s, j) => j === i ? { ...s, date: e.target.value } : s))}
                    className={`flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent outline-none ${theme === 'dark' ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'}`} />
                  <input type="time" value={slot.time} onChange={e => setScheduleSlots(prev => prev.map((s, j) => j === i ? { ...s, time: e.target.value } : s))}
                    className={`w-28 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent outline-none ${theme === 'dark' ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'}`} />
                </div>
              ))}
              <div>
                <label className={`block text-xs font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Note to Candidate (optional)</label>
                <textarea value={scheduleNote} onChange={e => setScheduleNote(e.target.value)} rows={2} placeholder="e.g. We look forward to speaking with you. Please confirm your availability."
                  className={`w-full px-3 py-2 border rounded-lg text-sm resize-none focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent outline-none ${theme === 'dark' ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'}`} />
              </div>
              <div className="flex gap-2 pt-1">
                <button onClick={() => setScheduleFor(null)} className={`flex-1 py-2.5 rounded-xl text-sm font-medium border transition-colors ${theme === 'dark' ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>Cancel</button>
                <button onClick={sendPrescreenRequest} disabled={sendingPrescreen === scheduleFor.id}
                  className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-[#40E0D0] text-black hover:bg-[#40E0D0]/90 transition-colors disabled:opacity-50">
                  {sendingPrescreen === scheduleFor.id ? 'Sending...' : 'Send to Candidate'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Slot modal */}
      {confirmFor && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4" onClick={() => setConfirmFor(null)}>
          <div className={`max-w-md w-full rounded-2xl shadow-2xl overflow-hidden ${theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`} onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-5 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center"><CheckCircle className="w-5 h-5 text-white" /></div>
                  <div>
                    <h2 className="text-base font-bold">Confirm Interview Time</h2>
                    <p className="text-white/80 text-xs mt-0.5">{confirmFor.app.first_name} {confirmFor.app.last_name} submitted their availability</p>
                  </div>
                </div>
                <button onClick={() => setConfirmFor(null)} className="p-2 hover:bg-white/20 rounded-lg transition-colors"><X className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="p-5 space-y-4">
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Select the time slot that works best. The candidate will receive an immediate confirmation.</p>
              {confirmFor.slots.length === 0 ? (
                <div className={`text-center py-6 rounded-xl ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}`}>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>No availability slots found yet.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {confirmFor.slots.map((s, i) => (
                    <button key={i} onClick={() => confirmPrescreen(s)} disabled={confirmingSlot}
                      className={`w-full flex items-center justify-between px-4 py-3.5 rounded-xl border-2 text-left transition-all group disabled:opacity-50 ${theme === 'dark' ? 'border-gray-700 hover:border-emerald-400 hover:bg-emerald-900/20' : 'border-gray-200 hover:border-emerald-400 hover:bg-emerald-50'}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${theme === 'dark' ? 'bg-gray-700 group-hover:bg-emerald-800/40' : 'bg-gray-100 group-hover:bg-emerald-100'}`}>
                          <Calendar className="w-4 h-4 text-emerald-500" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold">{new Date(s.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
                          <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{s.time}</p>
                        </div>
                      </div>
                      <span className={`text-xs font-semibold px-3 py-1 rounded-full transition-colors ${theme === 'dark' ? 'bg-gray-700 text-gray-400 group-hover:bg-emerald-700 group-hover:text-white' : 'bg-gray-100 text-gray-500 group-hover:bg-emerald-500 group-hover:text-white'}`}>
                        {confirmingSlot ? '...' : 'Confirm'}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              <button onClick={() => { setConfirmFor(null); setProposeTimesFor(confirmFor.app); }} className={`w-full py-2.5 rounded-xl text-sm font-medium border transition-colors flex items-center justify-center gap-2 ${theme === 'dark' ? 'border-gray-700 text-gray-400 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                <RefreshCw className="w-3.5 h-3.5" />None work — propose new times
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Propose New Times modal */}
      {proposeTimesFor && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4" onClick={() => setProposeTimesFor(null)}>
          <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-2xl shadow-2xl w-full max-w-lg p-6`} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="font-bold text-lg">Propose New Times</h3>
                <p className={`text-sm mt-0.5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Suggest up to 3 alternative time slots</p>
              </div>
              <button onClick={() => setProposeTimesFor(null)} className={`p-1.5 rounded-lg ${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3 mb-4">
              {proposeSlots.map((slot, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className={`text-xs font-semibold w-16 flex-shrink-0 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Option {i + 1}</span>
                  <input type="date" value={slot.date}
                    onChange={e => { const s = [...proposeSlots]; s[i] = { ...s[i], date: e.target.value }; setProposeSlots(s); }}
                    className={`flex-1 border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent outline-none ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`} />
                  <input type="time" value={slot.time}
                    onChange={e => { const s = [...proposeSlots]; s[i] = { ...s[i], time: e.target.value }; setProposeSlots(s); }}
                    className={`w-28 border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent outline-none ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`} />
                </div>
              ))}
            </div>
            <textarea value={proposeNote} onChange={e => setProposeNote(e.target.value)} rows={2}
              placeholder="Optional note to the candidate..."
              className={`w-full border rounded-xl px-3 py-2.5 text-sm resize-none focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent outline-none mb-4 ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'}`} />
            <div className="flex gap-2">
              <button onClick={sendProposeNewTimes} disabled={sendingPropose || proposeSlots.every(s => !s.date && !s.time)}
                className="flex-1 py-2.5 bg-[#40E0D0] text-black font-semibold rounded-xl hover:bg-[#40E0D0]/90 disabled:opacity-50 transition-colors">
                {sendingPropose ? 'Sending...' : 'Send Proposed Times'}
              </button>
              <button onClick={() => setProposeTimesFor(null)} className={`px-5 py-2.5 rounded-xl font-medium ${theme === 'dark' ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'}`}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Note modal */}
      {noteEntry && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-2xl w-full max-w-md p-6`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg">Add Note</h3>
              <button onClick={() => { setNoteEntry(null); setNoteDraft(''); }} className={`p-1.5 rounded-lg ${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}><X className="w-4 h-4" /></button>
            </div>
            <p className={`text-sm mb-3 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{noteEntry.consultant?.first_name} {noteEntry.consultant?.last_name}</p>
            <textarea value={noteDraft} onChange={e => setNoteDraft(e.target.value)} rows={4} placeholder="Add internal notes about this candidate..."
              className={`w-full border rounded-xl px-3 py-2.5 text-sm resize-none focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent outline-none ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`} />
            <div className="flex gap-2 mt-4">
              <button onClick={saveNote} disabled={savingNote} className="flex-1 py-2.5 bg-[#40E0D0] text-black font-semibold rounded-xl hover:bg-[#40E0D0]/90 disabled:opacity-50 transition-colors">{savingNote ? 'Saving...' : 'Save Note'}</button>
              <button onClick={() => { setNoteEntry(null); setNoteDraft(''); }} className={`px-5 py-2.5 rounded-xl font-medium ${theme === 'dark' ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'}`}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
